import { useState } from 'react';
import { ApiError, Button, ErrorNote, Sheet, api, brl, cx, haptic } from '@mobi/web';
import type { DriverRide } from '../useDriver.js';

/**
 * Fim da corrida, do lado do motorista.
 *
 * O número grande é o que ele tem que receber em dinheiro — já líquido do
 * saldo que o passageiro tinha na carteira. Mostrar a tarifa cheia aqui
 * faria o motorista cobrar a mais.
 */
export function FinishedRide({ ride, onDone }: { ride: DriverRide; onDone: () => void }) {
  const [stars, setStars] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const cash = ride.cashDueCents ?? ride.finalFareCents ?? ride.estimate.fareCents;

  async function submit() {
    setBusy(true);
    setError(null);
    try {
      if (stars > 0) await api(`/api/rides/${ride.id}/rate`, { body: { stars } });
      haptic();
      onDone();
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) onDone();
      else setError(err instanceof ApiError ? err.message : 'não foi possível avaliar');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex h-full flex-col justify-end bg-surface-2">
      <Sheet className="max-h-full overflow-y-auto">
        <p className="text-sm font-medium text-muted">Corrida concluída</p>

        <p className="text-xs font-semibold uppercase tracking-wide text-brand">
          receber em dinheiro
        </p>
        <p className="tabular mt-1 text-5xl font-bold">{brl(cash)}</p>

        {ride.walletAppliedCents > 0 && (
          <div className="mt-4 rounded-2xl bg-warn/10 px-4 py-3 text-sm text-warn">
            Atenção: a tarifa foi {brl(ride.finalFareCents ?? ride.estimate.fareCents)}, mas{' '}
            {brl(ride.walletAppliedCents)} foram abatidos do saldo do passageiro.
            <strong className="block"> Cobre apenas {brl(cash)}.</strong>
          </div>
        )}

        <div className="mt-8">
          <p className="font-semibold">
            Como foi o passageiro{ride.passenger ? ` ${ride.passenger.name.split(' ')[0]}` : ''}?
          </p>
          <div className="mt-3 flex gap-2">
            {[1, 2, 3, 4, 5].map((n) => (
              <button
                key={n}
                type="button"
                aria-label={`${n} estrela${n > 1 ? 's' : ''}`}
                onClick={() => {
                  setStars(n);
                  haptic();
                }}
                className="p-1"
              >
                <svg
                  viewBox="0 0 20 20"
                  className={cx('h-9 w-9 transition', n <= stars ? 'fill-warn' : 'fill-line')}
                >
                  <path d="M10 1.5l2.6 5.3 5.9.9-4.3 4.1 1 5.8L10 14.9 4.8 17.6l1-5.8L1.5 7.7l5.9-.9z" />
                </svg>
              </button>
            ))}
          </div>
        </div>

        {error && (
          <div className="mt-4">
            <ErrorNote>{error}</ErrorNote>
          </div>
        )}

        <div className="mt-6">
          <Button size="lg" full loading={busy} onClick={submit}>
            {stars > 0 ? 'Enviar e voltar a receber corridas' : 'Voltar a receber corridas'}
          </Button>
        </div>
      </Sheet>
    </div>
  );
}
